import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';
import { getErrorMessage } from '~shared/utils/error-handler';

import {
  type AddStudentToEntityOutcome,
  type AddStudentToEntityResponse,
  type EnrollmentPaymentStatus,
  type EnrollmentStudentResult,
  getAddStudentToEntityOutcome,
} from './add-student-to-entity-outcome';

export type {
  AddStudentToEntityOutcome,
  AddStudentToEntityResponse,
  EnrollmentPaymentStatus,
  EnrollmentStudentResult,
};
export { getAddStudentToEntityOutcome };

interface AddStudentToEntityParams {
  studioId: string;
  entityId: string;
  studentIds: Array<string>;
  entityType: 'class' | 'event';
  paymentMethod: 'card' | 'us_bank_account' | 'au_becs_debit' | null;
  // Enrollment-specific parameters
  discountCategory?: null | 'discount' | 'scholarship';
  discountType?: 'amount' | 'percentage';
  discountAmount?: number;
  discountPercentage?: number;
  applyDiscountTo?: 'first' | 'all';
  collectUpfront?: boolean;
  finalAmount?: number;
  sessionId?: string;
  skipPaymentMethod?: boolean;
  customCreditAmount?: number;
  allowCapacityOverride?: boolean;
}

export const addStudentToEntity = async (params: AddStudentToEntityParams) => {
  const response = await baseAPI.post<AddStudentToEntityResponse>(
    '/payment-processor/add-student-to-entity',
    params,
  );
  return response.data;
};

export const useAddStudentToEntity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addStudentToEntity,
    onSuccess: (data, params) => {
      // Only toast/invalidate if not redirecting to Stripe setup
      if (
        data.status === 200 &&
        data.message !== 'Payment method not found' &&
        !data.url &&
        !data.clientSecret
      ) {
        const outcome = getAddStudentToEntityOutcome(data);
        if (outcome.type === 'success') {
          toast.success(outcome.message);
        } else if (
          outcome.type === 'partial' ||
          outcome.type === 'already_enrolled'
        ) {
          toast.warning(outcome.message);
        } else {
          toast.error(outcome.message);
        }

        const enrolledCount =
          data.summary?.enrolled ??
          (data.results ?? []).filter(
            (result) => result.enrollmentStatus === 'enrolled',
          ).length;

        // ENR-3580: skip cache refresh when nothing enrolled (already_enrolled / false success).
        // Still refresh when enrolled but payment failed (outcome.type === 'error').
        if (outcome.type === 'already_enrolled' || enrolledCount === 0) {
          return;
        }

        // Invalidate relevant queries
        void queryClient.invalidateQueries({ queryKey: [KEYS.STUDENTS] });
        void queryClient.invalidateQueries({ queryKey: [KEYS.CLASSES] });
        void queryClient.invalidateQueries({ queryKey: [KEYS.EVENTS] });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.STUDIO_DETAILS_ALL],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.ENROLLMENT_TO_STUDENT_RATIO, params.sessionId],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.TOTAL_ACTIVE_CLASSES, params.sessionId],
        });
        void queryClient.invalidateQueries({
          queryKey: [
            KEYS.ENROLLMENT_PERCENTAGE_OVER_LAST_MONTH,
            params.sessionId,
          ],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.COLLECTED_REVENUE_TOTAL, params.sessionId],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.TOTAL_OUTSTANDING_REVENUE, params.sessionId],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.ACCOUNTS_RECEIVABLE, params.sessionId],
        });
        void queryClient.invalidateQueries({
          queryKey: [
            KEYS.ACCOUNTS_RECEIVABLE_STUDENT_GROUPING,
            params.sessionId,
          ],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.TOTAL_SCHEDULED_UPCOMING_REVENUE, params.sessionId],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.COLLECTED_REVENUE_MONTHLY_TREND, params.sessionId],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.TOTAL_ENROLLMENTS_OVER_TIME],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.STUDIO_DETAILS_ALL],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.TOP_10_ENROLLED_STUDENTS, params.sessionId],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.ENROLLMENT_TO_STUDENT_RATIO, params.sessionId],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.REVENUE_TREND, params.sessionId],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.ENROLLMENT_GROWTH, params.sessionId],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.PAYMENT_HEALTH, params.sessionId],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.CLASS_PERFORMANCE_LOW_ENROLLMENT, params.sessionId],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.CLASS_PERFORMANCE_NEAR_CAPACITY, params.sessionId],
        });
        void queryClient.invalidateQueries({
          queryKey: [
            KEYS.NINETY_PERCENT_FULL_CLASSES,
            params.sessionId,
          ].filter(Boolean),
        });

        // Keep event details/roster fresh in Studio after event enrollment.
        if (params.entityType === 'event') {
          void queryClient.invalidateQueries({
            queryKey: [KEYS.EVENTS, params.entityId],
          });
          void queryClient.invalidateQueries({
            queryKey: [KEYS.STUDENTS, params.entityId],
          });
        }
      }
    },
    onError: (error: unknown) => {
      // ENR-1947: toast the backend message (nested CustomExceptionFilter payload).
      // Do not invent "Payment failed." / "Enrollment failed." on the client.
      toast.error(getErrorMessage(error));
    },
  });
};
